import { useId, useState } from "react";

import { LodgeApiError } from "../api/client";
import type { OrganisationRecord, OrganisationRecordWrite } from "../api/hooks";
import {
  useCreateOrganisationRecord,
  useUpdateOrganisationRecord,
} from "../api/hooks";
import { SlideOver } from "../components/SlideOver";

import { OrganisationMentions } from "./-OrganisationMentions";

import styles from "./-OrganisationForm.module.scss";

// The fields an operator types. The customer link is not among them: pointing a
// record at an account is its own screen, with candidate ranking behind it, and
// a free-text uuid box here would be a way to get it wrong.
interface FormFields {
  name: string;
  kind: string;
  county: string;
  hectares: string;
  website: string;
  charityNumber: string;
  companyNumber: string;
}

function fieldsFrom(record: OrganisationRecord | null): FormFields {
  return {
    name: record?.name ?? "",
    kind: record?.kind ?? "",
    county: record?.county ?? "",
    // Empty rather than "0" when unknown: the box has to be able to say
    // "nobody has found out", which is what the column stores as null.
    hectares: record?.hectares === null || record === null ? "" : String(record.hectares),
    website: record?.website ?? "",
    charityNumber: record?.charityNumber ?? "",
    companyNumber: record?.companyNumber ?? "",
  };
}

// An empty box means unknown, not zero. Anything unparseable is left for the
// API to reject rather than silently becoming null, which would clear a value
// the operator thought they were setting.
function hectaresValue(entered: string): number | null | undefined {
  const trimmed = entered.trim();
  if (trimmed === "") return null;

  const parsed = Number(trimmed);

  return Number.isFinite(parsed) ? parsed : undefined;
}

// A message worth showing the operator: the API's own http.Error text, which is
// a short single line. A gateway's HTML error page is neither, and it arrives
// through the same channel.
function isReadableDetail(error: LodgeApiError): boolean {
  return (
    error.status >= 400 &&
    error.status < 500 &&
    error.detail !== "" &&
    error.detail.length <= 200 &&
    !error.detail.includes("<")
  );
}

export function OrganisationForm({
  record,
  onClose,
  onCreated,
  onCreatedFailureMessage,
  title,
}: {
  // Null creates; a record edits it.
  record: OrganisationRecord | null;
  onClose: () => void;
  // Something else to do with the record once it exists - the pipeline puts it
  // straight into the pipeline. Rejecting means the record was written and the
  // rest was not, which the form says rather than closing as though it had all
  // worked.
  onCreated?: (created: OrganisationRecord) => Promise<unknown>;
  // What to tell the operator when the record was written and onCreated was
  // not. Supplied by the caller because only it knows what the second half
  // was: a sentence naming the pipeline is wrong for the next caller.
  onCreatedFailureMessage?: (created: OrganisationRecord) => string;
  // What the panel is called when adding. The default is the organisations
  // page's; the pipeline is adding to a pipeline, and saying so is the
  // difference between the two entry points.
  title?: string;
}) {
  const [fields, setFields] = useState<FormFields>(() => fieldsFrom(record));
  const [message, setMessage] = useState("");

  // Document-global ids would collide the moment this panel is composed onto a
  // page that already has a "name" field, and the collision silently steals the
  // label association rather than failing.
  const fieldId = useId();

  const create = useCreateOrganisationRecord();
  const update = useUpdateOrganisationRecord();

  // The second write, which the mutations know nothing about. Without it the
  // dialog would let Escape unmount the form while onCreated is in flight,
  // taking the only report of a half-written add with it.
  const [handingOn, setHandingOn] = useState(false);

  // The record that was written when the rest was not. Saving again would write
  // a second one - nothing stops two records with the same name - so the form
  // stops offering that and offers the only recovery that does not: running
  // the second half again against the record that already exists.
  //
  // The fields go read-only with it. A retry sends the record's id and nothing
  // else, so an edit typed into a still-editable box would be dropped without
  // a word.
  const [halfWritten, setHalfWritten] = useState<OrganisationRecord | null>(null);

  const saving = create.isPending || update.isPending || handingOn;

  function setField(field: keyof FormFields, value: string) {
    setFields((previous) => ({ ...previous, [field]: value }));
  }

  // The second half, run once after the record is created and again if the
  // operator retries. Retrying is the only recovery that does not write a
  // second record.
  function handOn(created: OrganisationRecord) {
    if (onCreated === undefined) {
      onClose();
      return;
    }

    setMessage("");
    setHandingOn(true);

    void onCreated(created).then(
      () => {
        setHandingOn(false);
        onClose();
      },
      () => {
        setHandingOn(false);
        // The record exists by now: saying "that could not be saved" sends the
        // operator to type it a second time.
        setHalfWritten(created);
        setMessage(
          onCreatedFailureMessage?.(created) ??
            `${created.name} was added, but what came next did not.`,
        );
      },
    );
  }

  function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setMessage("");

    const hectares = hectaresValue(fields.hectares);
    if (hectares === undefined) {
      setMessage("Size must be a number, or empty if you do not know it");
      return;
    }

    const write: OrganisationRecordWrite = {
      name: fields.name.trim(),
      kind: fields.kind.trim(),
      county: fields.county.trim(),
      hectares,
      website: fields.website.trim(),
      charityNumber: fields.charityNumber.trim(),
      companyNumber: fields.companyNumber.trim(),
      // Preserved rather than sent as null: this form does not edit the link,
      // and the write shape is the whole record, so omitting it would unlink
      // a customer as a side effect of fixing their county.
      organisationId: record?.organisationId ?? null,
    };

    const onError = (error: unknown) => {
      // The server's own words where it gave them: it knows whether the county
      // was too long or the customer was already linked, and this form does not.
      //
      // Only for a 4xx, and only when it reads like a sentence. LodgeApiError
      // is built for every non-2xx, so a 502 from a proxy would otherwise put a
      // page of HTML in the alert box.
      if (error instanceof LodgeApiError && isReadableDetail(error)) {
        setMessage(error.detail);
        return;
      }

      setMessage("That could not be saved. Try again.");
    };

    if (record === null) {
      create.mutate(write, {
        onSuccess: (created) => {
          if (onCreated === undefined) {
            onClose();
            return;
          }

          handOn(created);
        },
        onError,
      });
      return;
    }

    update.mutate({ id: record.id, record: write }, { onSuccess: onClose, onError });
  }

  return (
    <SlideOver
      title={record === null ? (title ?? "Add an organisation") : record.name}
      onClose={onClose}
      busy={saving}
    >
      <form className={styles.form} onSubmit={onSubmit}>
        <div className={styles.field}>
          <label className={styles.label} htmlFor={`${fieldId}-name`}>
            Name
          </label>
          <input
            id={`${fieldId}-name`}
            className={styles.input}
            readOnly={halfWritten !== null}
            required
            value={fields.name}
            onChange={(event) => setField("name", event.target.value)}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor={`${fieldId}-kind`}>
            Kind
          </label>
          <input
            id={`${fieldId}-kind`}
            className={styles.input}
            readOnly={halfWritten !== null}
            placeholder="Nature reserve, community land trust…"
            value={fields.kind}
            onChange={(event) => setField("kind", event.target.value)}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor={`${fieldId}-county`}>
            County
          </label>
          <input
            id={`${fieldId}-county`}
            className={styles.input}
            readOnly={halfWritten !== null}
            value={fields.county}
            onChange={(event) => setField("county", event.target.value)}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor={`${fieldId}-hectares`}>
            Size in hectares
          </label>
          <input
            id={`${fieldId}-hectares`}
            className={styles.input}
            readOnly={halfWritten !== null}
            inputMode="decimal"
            placeholder="Leave empty if unknown"
            aria-describedby={`${fieldId}-hectares-hint`}
            value={fields.hectares}
            onChange={(event) => setField("hectares", event.target.value)}
          />
          <span id={`${fieldId}-hectares-hint`} className={styles.hint}>
            What they claim about themselves, not a mapped area
          </span>
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor={`${fieldId}-website`}>
            Website
          </label>
          <input
            id={`${fieldId}-website`}
            className={styles.input}
            readOnly={halfWritten !== null}
            value={fields.website}
            onChange={(event) => setField("website", event.target.value)}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor={`${fieldId}-charityNumber`}>
            Charity number
          </label>
          <input
            id={`${fieldId}-charityNumber`}
            className={styles.input}
            readOnly={halfWritten !== null}
            value={fields.charityNumber}
            onChange={(event) => setField("charityNumber", event.target.value)}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor={`${fieldId}-companyNumber`}>
            Company number
          </label>
          <input
            id={`${fieldId}-companyNumber`}
            className={styles.input}
            readOnly={halfWritten !== null}
            value={fields.companyNumber}
            onChange={(event) => setField("companyNumber", event.target.value)}
          />
        </div>

        {message !== "" && (
          <div className={styles.error} role="alert">
            {message}
          </div>
        )}

        {/* Only when editing: a record being created has no id to hang
            mentions off, and nothing to show against it. */}
        {record !== null && <OrganisationMentions recordId={record.id} />}

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.cancel}
            disabled={saving}
            onClick={onClose}
          >
            {halfWritten !== null ? "Done" : "Cancel"}
          </button>
          {/* Withdrawn once the record exists: pressing it again is the obvious
              thing to do after reading the message, and it would write a second
              record - nothing stops two with the same name. */}
          {halfWritten === null ? (
            <button type="submit" className={styles.save} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </button>
          ) : (
            // Against the record that already exists, so a retry cannot write a
            // second one. Sending the operator elsewhere to finish the job is
            // what walks them into that.
            <button
              type="button"
              className={styles.save}
              disabled={saving}
              onClick={() => handOn(halfWritten)}
            >
              {saving ? "Trying…" : "Try again"}
            </button>
          )}
        </div>
      </form>
    </SlideOver>
  );
}
